import React, {ChangeEvent, useState, useRef} from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import b_ from 'b_';

import I18N from 'assets/i18n/brandlift-project';

import {SurveyScope} from 'client/common/types';

import {
    Button,
    RadioButton,
    SelectWithForwardView as Select,
    Spin,
    Textinput,
    Tumbler,
    Icon,
    Tooltip
} from 'client/common/components/lego';
import {ModalProps} from 'client/common/components/survey-settings/types';

import {useMetrikaCounters} from 'client/common/hooks/metrika';
import {useSurvey, useSurveySettings, useUpdateSurveySettings} from 'client/common/hooks/survey';
import {useIsAuthorized, useIsSuperuser} from 'client/common/hooks/user';
import {useCanEditSurveySettings} from 'client/common/hooks/edit-mode';

import {genShareLink} from 'client/common/utils/survey';

import BlockInput from 'client/common/components/block-input';
import SurveyLanguage from 'client/common/components/survey-settings/survey-language';
import SurveyTextDirectionSelect
    from 'client/common/components/survey-settings/survey-text-direction-select/';
import SurveyRedirect from 'client/common/components/survey-settings/survey-redirect';
import ScopeSelect from 'client/common/components/survey-settings/modals/edit-settings/scope-select';

import {useSettingsMode} from './../../hooks';
import EditSettingsInverviews from './edit-settings-interviews';
import SettingsRedirect from './settings-redirect';

import './edit-settings.css';


const i18n = I18N.common.surveySettings.editSettingsModal;
const copyI18N = I18N.common.copyButton;

const b = b_.with('edit-settings-modal');

type ShareType = 'link' | 'web';

function getShareText(shareLink: string, shareType: ShareType) {
    if (shareType === 'web') {
        return `<iframe src="${shareLink}" style="border: none;"></iframe>`;
    }

    return shareLink;
}

const HelpPopupContent = () => {
    return (
        <div>
            <h5 className={b('help-title')}>{i18n.metricsHelpTitle}</h5>
            <div
                className={b('help-description')}
                dangerouslySetInnerHTML={{__html: i18n.metricsHelpDescription}}
            />
        </div>
    );
};

// eslint-disable-next-line max-statements, complexity
export default function EditSettingsModal(props: ModalProps) {
    const {slug} = props;

    const survey = useSurvey(slug);
    const mode = useSettingsMode(slug);
    const counters = useMetrikaCounters();
    const isAuthorized = useIsAuthorized();
    const isSuperuser = useIsSuperuser();

    const canEdit = useCanEditSurveySettings();

    const onUpdate = useUpdateSurveySettings(slug);

    const settings = useSurveySettings();

    const [scope, setScope] = useState(settings?.scope[0]);
    const [metrikaCounters, setMetrikaCounters] = useState(settings?.metrika);
    const [metrikaName, setMetrikaName] = useState(settings?.metrikaTitle);
    const [respReuse, setRespReuse] = useState(settings?.respReuse);
    const [shareType, setSharetype] = useState<ShareType>('link');

    const [isCopyTooltipVisible, setIsCopyTooltipVisible] = useState(false);
    const copyIcon = useRef(null);

    if (survey.status !== 'success') {
        return <Spin className={b('spin')} size='s' view='default' progress />;
    }

    const metrikaCounterOptions = (counters.data || []).map(({id, name}) => ({
        value: id.toString(),
        content: name
    }));

    const handleMetrikaCountersChange = async (event: ChangeEvent<HTMLSelectElement>) => {
        const newMetrikaCounters = (event.target.value as unknown) as string[];

        setMetrikaCounters(newMetrikaCounters);
        await onUpdate({
            metrika: newMetrikaCounters
        });
    };

    const handleMetrikaNameChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const newMetrikaName = event.target.value;

        setMetrikaName(newMetrikaName);
        await onUpdate({
            metrikaTitle: metrikaName
        });
    };

    const handleRespReuseChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const newRespReuse = event.target.checked;

        setRespReuse(newRespReuse);
        await onUpdate({
            respReuse: newRespReuse
        });
    };

    const handleScopeChange = async (event: ChangeEvent<HTMLSelectElement>) => {
        const newScope = event.target.value as SurveyScope;

        setScope(newScope);
        await onUpdate({
            ...(newScope !== undefined && {scope: [newScope]})
        });
    };

    const onCopyShareText = () => {
        setIsCopyTooltipVisible(true);
        setTimeout(() => setIsCopyTooltipVisible(false), 1000);
    };

    const shareLink = genShareLink(slug, Boolean(respReuse), scope, survey.data.box?.name);
    const shareText = getShareText(shareLink, shareType);

    return (
        <div className={b('')}>
            <h2 className={b('title')}>{i18n.title}</h2>

            <p className={b('description')}>{i18n.subtitle}</p>

            <SurveyLanguage />

            {isSuperuser && <SurveyTextDirectionSelect />}

            {isAuthorized && (
                <ScopeSelect scope={scope} handleScopeChange={handleScopeChange} />
            )}

            <BlockInput
                popupContent={<HelpPopupContent />}
                title={i18n.metrikaCounters}
            >
                <Select
                    checkable={false}
                    className={b('select-control')}
                    disabled={!canEdit || !metrikaCounterOptions.length}
                    onChange={handleMetrikaCountersChange}
                    options={metrikaCounterOptions}
                    size='m'
                    value={metrikaCounters ?? []}
                    view='dl-clear'
                    multiple
                />
            </BlockInput>

            {metrikaCounters && metrikaCounters.length > 0 && (
                <BlockInput className={b('input-wrapper')} title={i18n.metrikaName}>
                    <Textinput
                        className={b('input-control')}
                        disabled={!canEdit}
                        onChange={handleMetrikaNameChange}
                        size='m'
                        value={metrikaName}
                        view='dl'
                    />
                </BlockInput>
            )}

            <SurveyRedirect mix={b('input-wrapper')} />

            {isAuthorized && <SettingsRedirect />}

            <BlockInput className={b('input-wrapper')}>
                <Tumbler
                    className={b('tumbler')}
                    checked={Boolean(respReuse)}
                    labelAfter={i18n.allowRespReuse}
                    onChange={handleRespReuseChange}
                    disabled={!canEdit}
                    size='m'
                    view='dl'
                />
            </BlockInput>

            <BlockInput className={b('input-wrapper')}>
                <RadioButton
                    onChange={event => setSharetype(event.target.value as ShareType)}
                    options={[
                        {value: 'link', children: i18n.share.link.label},
                        {value: 'web', children: i18n.share.web.label}
                    ]}
                    size='m'
                    value={shareType}
                    view='dl'
                />
            </BlockInput>

            <BlockInput
                title={i18n.share[shareType].title}
                description={i18n.share[shareType].subtitle}
            >
                <div className={b('share-input', {view: 'tabs'})}>
                    <Textinput
                        className={b('input-control')}
                        size='m'
                        view='dl'
                        disabled
                        value={shareText}
                    />

                    <CopyToClipboard onCopy={onCopyShareText} text={shareText}>
                        <Button
                            className={b('copy-icon')}
                            icon={(mix: string) => <Icon className={mix} type='copy-active' />}
                            innerRef={copyIcon}
                            size='icon'
                            view='clear'
                        />
                    </CopyToClipboard>
                </div>

                <Tooltip
                    size='s'
                    view='default'
                    anchor={copyIcon}
                    visible={isCopyTooltipVisible}
                    zIndex={1010}
                >
                    {copyI18N.copied}
                </Tooltip>
            </BlockInput>

            {isAuthorized && mode !== 'ux' && <EditSettingsInverviews {...props} />}
        </div>
    );
}
